import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { useKeenSlider } from 'keen-slider/react';
import ReactPlayer from 'react-player';
import 'keen-slider/keen-slider.min.css';
import { window } from 'browser-monads';
import Icon from '../components/Icon';
import IconButton from '@material-ui/core/IconButton';
import Module from '../components/Module';
import CtaButton from '../components/CtaButton';
import Text from '../components/Text';
import { useMediaQuery } from '@material-ui/core';
import theme from '../site/theme';

// Define styles using Material-UI's makeStyles
const useStyles = makeStyles(theme => ({
  root: {
    marginLeft: '68px !important',
    [theme.breakpoints.down('sm')]: {
      marginLeft: '16px !important',
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '40px 68px 32px 0px',
    [theme.breakpoints.down('sm')]: {
      marginRight: 16,
    },
  },
  title: {
    [theme.breakpoints.down('sm')]: {
      '&:before': {
        marginLeft: 'auto',
        marginRight: 'auto',
      },
    },
  },
  sliderNav: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  navBtn: {
    width: 48,
    height: 48,
    transition: 'filter 0.2s ease',
    '&:hover': {
      filter: 'brightness(70%)',
    },
  },
  rotateIcon: {
    transform: 'scaleX(-1)',
    '&:active': {
      transform: 'scaleX(-1)',
    },
  },
  videoSlider: {
    position: 'relative',
  },
  videoSlide: {
    display: 'flex',
    justifyContent: 'center',
    overflow: 'visible',
    minWidth: '302px',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  videoPreview: {
    position: 'relative',
    overflow: 'hidden',
    height: 433,
  },
  videoPlayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    '& video': {
      objectFit: 'cover !important',
    },
  },
  playBtn: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    cursor: 'pointer',
    transition: 'opacity 0.3s ease',
    height: 40,
    width: 40,
    opacity: 1,
  },
  hidden: {
    opacity: 0,
  },
  ctaStatus: {
    position: 'absolute',
    bottom: 10,
    left: 10,
    background: '#ff2765',
    color: 'white',
    padding: '5px 10px',
    fontFamily: '"Spezia"',
    lineHeight: 1.27,
    fontSize: 12,
    fontWeight: 400,
    textTransform: 'uppercase',
    transition: 'transform 0.2s ease-in-out',
  },
  ctaHidden: {
    transform: 'translate(20%, 300%) scale(1.5)',
  },
  ctaVisible: {
    transform: 'translate(0, 0) scale(1)',
  },
  videoInfo: {
    padding: 16,
  },
  collectionTitle: {
    fontFamily: '"Druk Wide Web"',
    fontSize: 20,
    fontWeight: 700,
    lineHeight: '140%',
    color: '#581D70',
  },
  videoModal: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(0, 0, 0, 0.95)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2147483645,
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      padding: 'env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)',
      overscrollBehavior: 'none',
    },
  },
  videoContent: {
    position: 'relative',
    background: 'black',
    padding: '68px 0',
    width: '100%',
    height: '100%',
    maxHeight: 1060,
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      padding: 0,
    },
    '@media (max-height: 960px) and (min-width: 600px)': {
      padding: 20,
    },
  },
  videoContentAnimation: {
    display: 'grid',
    gridTemplateColumns: '1fr auto auto 1fr',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    maxWidth: '1350px',
    margin: '0 auto',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  zoomIn: {
    animation: '$zoomIn 0.3s ease forwards',
  },
  zoomOut: {
    animation: '$zoomOut 0.3s ease forwards',
  },
  '@keyframes zoomIn': {
    from: {
      transform: 'scale(0.4)',
      opacity: 0,
    },
    to: {
      transform: 'scale(1)',
      opacity: 1,
    },
  },
  '@keyframes zoomOut': {
    from: {
      transform: 'scale(1)',
      opacity: 1,
    },
    to: {
      transform: 'scale(0.4)',
      opacity: 0,
    },
  },
  fullVideoSlider: {
    maxWidth: 520,
    width: '100%',
    height: '100%',
    aspectRatio: '9/16',
    position: 'relative',
    gridColumn: 2,
    borderRadius: 12,
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      height: '100dvh',
      width: '100%',
      aspectRatio: 'unset',
      maxWidth: '100%',
      borderRadius: 0,
    },
  },
  videoContainer: {
    position: 'relative',
    display: 'flex',
    gap: 10,
    width: '100%',
    height: '100%',
  },
  fullVideo: {
    borderRadius: 12,
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      overflow: 'visible',
      borderRadius: 0,
    },
    '& video': {
      objectFit: 'cover !important',
      objectPosition: 'center',
    },
  },
  spinner: {
    width: 56,
    height: 56,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  dot: {
    position: 'absolute',
    width: 12,
    height: 12,
    backgroundColor: '#FFFFFF',
    borderRadius: '50%',
    animation: '$fade 1.2s ease-in-out infinite',
  },
  dot1: {
    transform: 'rotate(0deg) translate(20px)',
    animationDelay: '0s',
  },
  dot2: {
    transform: 'rotate(120deg) translate(20px)',
    animationDelay: '-0.4s',
  },
  dot3: {
    transform: 'rotate(240deg) translate(20px)',
    animationDelay: '-0.8s',
  },
  '@keyframes fade': {
    '0%, 100%': {
      opacity: 0.3,
    },
    '50%': {
      opacity: 1,
    },
  },
  bufferingSpinner: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 20,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  videoProgressBar: {
    position: 'absolute',
    bottom: 16,
    left: '50%',
    transform: 'translateX(-50%)',
    maxWidth: '90%',
    width: '100%',
    height: 16,
    zIndex: 10,
    cursor: 'pointer',
  },
  videoProgressTrack: {
    position: 'absolute',
    top: '50%',
    left: 0,
    width: '100%',
    height: 3,
    background: '#808080',
    transform: 'translateY(-50%)',
  },
  videoProgressKnob: {
    position: 'absolute',
    top: '50%',
    width: 12,
    height: 12,
    background: '#FF0000',
    borderRadius: '50%',
    transform: 'translate(-50%, -50%)',
    '&.dragging': {
      background: '#ff3658',
      transform: 'translate(-50%, -50%) scale(1.2)',
    },
  },
  modalFooter: {
    position: 'absolute',
    bottom: -2,
    left: 0,
    background: 'linear-gradient(0deg, #000 40%, transparent 100%)',
    height: '30%',
    width: '100%',
  },
  btnVideoImpact: {
    position: 'absolute',
    bottom: 52,
    left: '50%',
    width: '100%',
    maxWidth: '90%',
    transform: 'translateX(-50%)',
    alignItems: 'center',
    margin: 0,
    display: 'flex',
    justifyContent: 'center',
    borderRadius: 6,
    '& button': {
      width: '100%',
      height: 60,
    },
  },
  playPauseOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    opacity: 0,
    transition: 'opacity 0.3s ease',
    pointerEvents: 'none',
  },
  playPauseOverlayVisible: {
    opacity: 1,
  },
  playPauseIcon: {
    animation: '$pop 0.3s ease forwards',
  },
  '@keyframes pop': {
    '0%': {
      transform: 'scale(0)',
      opacity: 0,
    },
    '50%': {
      transform: 'scale(1.2)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(1)',
      opacity: 0.8,
    },
  },
  controlPanel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    transition: 'opacity 0.3s ease',
    [theme.breakpoints.down('sm')]: {
      position: 'absolute',
      top: 16,
      right: 8,
    },
  },
  navPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    justifyContent: 'center',
    gridColumn: 4,
    marginLeft: 'auto',
    marginRight: 16,
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  rotateIcon180: {
    transform: 'rotate(180deg)',
    '&:active': {
      transform: 'rotate(180deg)',
    },
  },
  controlBtn: {
    width: 56,
    height: 56,
    cursor: 'pointer',
    transition: 'filter 0.2s ease',
    '&:hover': {
      filter: 'brightness(2) contrast(2)',
    },
  },
  shareBtn: {
    marginTop: 'auto',
    marginBottom: 70,
    width: 48,
    height: 48,
  },
  disabled: {
    opacity: 0.5,
    pointerEvents: 'none',
  },
}));

const VideoImpactModule = (props) => {
  const classes = useStyles();
  const {title, item, repo} = props;
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isMuted, setIsMuted] = useState(true);
  const [duration, setDuration] = useState(0);
  const [modalVisible, setModalVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const [closing, setClosing] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullScreenSliderReady, setIsFullScreenSliderReady] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedProgress, setDraggedProgress] = useState(0);

  const trackRef = useRef(null);
  const animationFrameIdRef = useRef(null);
  const lastUpdateTimeRef = useRef(0);
  const playerRefs = useRef([]);
  const isAnimatingRef = useRef(false);
  const isPlayingRef = useRef(false);
  const isPausedRef = useRef(false);
  const isBufferingRef = useRef(false);
  const maxPlayedSecondsRef = useRef(0);
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      if (!isIOS) {
        setIsMuted(false);
      }
    }
  }, []);

  const handleTrackClick = (e) => {
    e.stopPropagation();
    const trackRect = trackRef.current.getBoundingClientRect();
    const clickPosition = e.clientX - trackRect.left;
    const trackWidth = trackRect.width;
    const newProgress = (clickPosition / trackWidth) * 100;
    const seekTime = (newProgress / 100) * duration;
    const currentPlayer = playerRefs.current[selectedVideo];
    if (currentPlayer) {
      currentPlayer.seekTo(seekTime);
    }
    setProgress(newProgress);
    maxPlayedSecondsRef.current = seekTime;
  };

  const handleMouseDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDragging(true);
    setIsPaused(true);
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    const trackRect = trackRef.current.getBoundingClientRect();
    const clickPosition = e.clientX - trackRect.left;
    const trackWidth = trackRect.width;
    const newProgress = (clickPosition / trackWidth) * 100;
    setDraggedProgress(Math.min(Math.max(newProgress, 0), 100));
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      const currentPlayer = playerRefs.current[selectedVideo];
      if (currentPlayer) {
        const seekTime = (draggedProgress / 100) * duration;
        currentPlayer.seekTo(seekTime);
        setProgress(draggedProgress);
        maxPlayedSecondsRef.current = seekTime;
      }
      setIsPaused(false);
    }
  }, [isDragging, selectedVideo, draggedProgress, duration]);

  const handleTouchStart = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDragging(true);
    setIsPaused(true);
  };

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    const trackRect = trackRef.current.getBoundingClientRect();
    const touch = e.touches[0];
    const touchPosition = touch.clientX - trackRect.left;
    const trackWidth = trackRect.width;
    const newProgress = (touchPosition / trackWidth) * 100;
    setDraggedProgress(Math.min(Math.max(newProgress, 0), 100));
  }, [isDragging]);

  const handleTouchEnd = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      const currentPlayer = playerRefs.current[selectedVideo];
      if (currentPlayer) {
        const seekTime = (draggedProgress / 100) * duration;
        currentPlayer.seekTo(seekTime);
        setProgress(draggedProgress);
        maxPlayedSecondsRef.current = seekTime;
      }
      setIsPaused(false);
    }
  }, [isDragging, selectedVideo, draggedProgress, duration]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  const [sliderRef, slider] = useKeenSlider({
    loop: true,
    mode: 'free',
    slides: {
      perView: 'auto',
      spacing: 8,
    },
    breakpoints: {
      '(max-width: 600px)': {slides: {perView: 'auto', spacing: 8}},
    },
  });

  // Initialize Keen Slider for the fullscreen modal slider
  const [fullScreenSliderRef, fullScreenSlider] = useKeenSlider({
    loop: true,
    vertical: true,
    slides: {perView: 1, spacing: 8},
    created() {
      setIsFullScreenSliderReady(true);
    },
    slideChanged(s) {
      const virtualIndex = s.track.details.abs;
      const videoCount = videoItems.length;
      const realIndex = ((virtualIndex % videoCount) + videoCount) % videoCount;
      setSelectedVideo(realIndex);
      setProgress(0);
      maxPlayedSecondsRef.current = 0;
      lastUpdateTimeRef.current = 0;
      setIsPaused(false);
      isPausedRef.current = false;
      setIsPlaying(false);
      isPlayingRef.current = false;
      isAnimatingRef.current = false;

      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }

      const currentPlayer = playerRefs.current[realIndex];
      if (currentPlayer) {
        currentPlayer.seekTo(0);
      }
    },
    drag: true,
    mode: 'free-snap',
    animation: {
      duration: 300,
      easing: (t) => t * t * (3 - 2 * t),
    },
  });

  // Effect to handle modal visibility and reset states on close
  useEffect(() => {
    if (selectedVideo !== null && !modalVisible) {
      setModalVisible(true);
      setProgress(0);
      maxPlayedSecondsRef.current = 0;
      lastUpdateTimeRef.current = 0;
      setIsBuffering(true);
    } else if (closing) {
      setIsPlaying(false);
      isPlayingRef.current = false;
      setIsFullScreenSliderReady(false);
      setIsBuffering(false);
      isBufferingRef.current = false;
      const timer = setTimeout(() => {
        setModalVisible(false);
        setClosing(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [selectedVideo, closing, modalVisible]);

  // Effect to move the fullscreen slider to the selected video
  useEffect(() => {
    if (modalVisible && isFullScreenSliderReady && fullScreenSlider?.current && selectedVideo !== null) {
      fullScreenSlider.current.moveToIdx(selectedVideo, true, {duration: 0});
    }
  }, [modalVisible, isFullScreenSliderReady, selectedVideo]);

  // Effect to prevent scrolling when the modal is open
  useEffect(() => {
    if (modalVisible) {
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100vh';
    } else {
      document.body.style.overflow = '';
      document.body.style.height = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.height = '';
    };
  }, [modalVisible]);

  // Effect to sync the paused state with the ref and manage animation frame
  useEffect(() => {
    isPausedRef.current = isPaused;
    if (isPaused) {
      isPlayingRef.current = false;
      setIsPlaying(false);
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
    }
  }, [isPaused]);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    isBufferingRef.current = isBuffering;
  }, [isBuffering]);

  useEffect(() => {
    const tick = () => {
      if (
        !isPlayingRef.current ||
        !modalVisible ||
        isPausedRef.current ||
        isBufferingRef.current ||
        !duration ||
        selectedVideo === null
      ) {
        if (animationFrameIdRef.current) {
          cancelAnimationFrame(animationFrameIdRef.current);
          animationFrameIdRef.current = null;
        }
        return;
      }

      const now = performance.now();
      if (now - lastUpdateTimeRef.current >= 100) {
        const currentTime = performance.now();
        const elapsedTime = (currentTime - lastUpdateTimeRef.current) / 1000;
        maxPlayedSecondsRef.current += elapsedTime;
        const newProgress = (maxPlayedSecondsRef.current / duration) * 100;
        setProgress(Math.min(newProgress, 100));
        lastUpdateTimeRef.current = currentTime;

        if (newProgress >= 100) {
          setProgress(0);
          maxPlayedSecondsRef.current = 0;
          lastUpdateTimeRef.current = currentTime;
          setIsPlaying(false);
          isPlayingRef.current = false;
        }
      }

      animationFrameIdRef.current = requestAnimationFrame(tick);
    };

    if (
      isPlaying &&
      modalVisible &&
      !isPaused &&
      !isBuffering &&
      selectedVideo !== null &&
      duration > 0
    ) {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
      animationFrameIdRef.current = requestAnimationFrame(tick);
    } else {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
    }

    return () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
    };
  }, [isPlaying, modalVisible, isPaused, isBuffering, selectedVideo, duration]);

  const handleProgress = (state, index) => {
    if (selectedVideo === null || !modalVisible || index !== selectedVideo) return;

    if (state.playedSeconds === 0 && maxPlayedSecondsRef.current > 0) {
      return;
    }

    maxPlayedSecondsRef.current = Math.max(maxPlayedSecondsRef.current, state.playedSeconds);

    const newProgress = (maxPlayedSecondsRef.current / duration) * 100;
    setProgress(newProgress);
    lastUpdateTimeRef.current = performance.now();
  };

  // Handler for setting video duration
  const handleDuration = (duration) => {
    setDuration(duration);
  };

  // Handler for playing the video
  const handlePlay = (index) => {
    if (index === selectedVideo) {
      setIsPlaying(true);
      isPlayingRef.current = true;
      lastUpdateTimeRef.current = performance.now();
    }
  };

  const handlePause = (index) => {
    if (index === selectedVideo && !isBuffering) {
      setIsPlaying(false);
      isPlayingRef.current = false;
      setIsPaused(true);
      isPausedRef.current = true;
    }
  };

  const handleCloseModal = () => {
    setClosing(true);
    setSelectedVideo(null);
    setIsPaused(false);
    isPausedRef.current = false;
    setIsPlaying(false);
    isPlayingRef.current = false;
    setProgress(0);
    maxPlayedSecondsRef.current = 0;
    lastUpdateTimeRef.current = 0;
    if (animationFrameIdRef.current) {
      cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = null;
    }
  };

  const togglePlayPause = () => {
    if (!isBuffering) {
      setIsPaused((prev) => {
        const newPaused = !prev;
        if (newPaused) {
          setIsPlaying(false);
          isPlayingRef.current = false;
        } else {
          setIsPlaying(true);
          isPlayingRef.current = true;
        }
        return newPaused;
      });
    }
  };

  const handleShare = () => {
    if (typeof window !== 'undefined' && navigator.share) {
      navigator.share({
        title: 'Check out this video!',
        url: window.location.pathname,
      });
    }
  };

  const handlePrev = () => {
    if (!isAnimatingRef.current) {
      isAnimatingRef.current = true;
      fullScreenSlider?.current?.prev();
    }
  };

  const handleNext = () => {
    if (!isAnimatingRef.current) {
      isAnimatingRef.current = true;
      fullScreenSlider?.current?.next();
    }
  };

  const isValidVideoItem = (video) => {
    if (!video) {
      return false;
    }
    const {product, short_video_url, complete_video_url} = video.field_value;
    if (
      !product.field_value.collection_title.field_value ||
      !product.field_value.status.field_value
    ) {
      return false;
    }
    if (!short_video_url || !short_video_url.field_value) {
      return false;
    }

    if (!complete_video_url || !complete_video_url.field_value) {
      return false;
    }

    return true;
  };

  const videoItems = (item?.field_value || []).filter(isValidVideoItem);

  return (
    <Module className={classes.root} variant="full">
      <div className={classes.header}>
        <Text
          component="h2"
          variant="titleXXXL"
          weight="bold"
          className={classes.title}
          overline
        >
          {title?.field_value || ''}
        </Text>
        <div className={classes.sliderNav}>
          <IconButton
            size="small"
            onClick={() => slider?.current?.prev()}
            aria-label="nav-left"
          >
            <Icon className={`${classes.navBtn} icon`} name="slider_left_tangerine"/>
          </IconButton>
          <IconButton
            size="small"
            onClick={() => slider?.current?.next()}
            aria-label="nav-right"
          >
            <Icon className={`${classes.navBtn} icon`} name="slider_right_tangerine"/>
          </IconButton>
        </div>
      </div>

      {videoItems.length > 0 && (
        <div style={{zIndex: 2, position: 'relative', overflowY: 'visible'}}>
          <div ref={sliderRef} className={`keen-slider ${classes.videoSlider}`}>
            {videoItems.map((video, index) => {
              const {product, short_video_url, cta} = video.field_value;
              const collectionTitle = product.field_value.collection_title.field_value;

              return (
                <div
                  key={index}
                  className={`keen-slider__slide ${classes.videoSlide}`}
                  onMouseEnter={!isMobile ? () => setHoveredIndex(index) : null}
                  onMouseLeave={!isMobile ? () => setHoveredIndex(null) : null}
                  onClick={() => setSelectedVideo(index)}
                >
                  <div className="video-item">
                    <div className={classes.videoPreview}>
                      <ReactPlayer
                        url={short_video_url.field_value}
                        playing={!isMobile && hoveredIndex === index}
                        muted={true}
                        loop={true}
                        width="100%"
                        height="100%"
                        className={classes.videoPlayer}
                        playsinline
                      />
                      <Icon
                        className={`${classes.playBtn} ${!isMobile && hoveredIndex === index ? classes.hidden : ''}`}
                            name="play_black"/>
                      <Icon
                        className={`${classes.playBtn} ${!isMobile && hoveredIndex === index ? '' : classes.hidden}`}
                            name="full_screen"/>
                      <div
                        className={`${classes.ctaStatus} ${!isMobile && hoveredIndex === index ? classes.ctaVisible : classes.ctaHidden}`}>
                        {cta.field_value.label || ''}
                      </div>
                    </div>
                    <div className={classes.videoInfo}>
                      <div className={classes.collectionTitle}>{collectionTitle}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {modalVisible && (
        <div className={classes.videoModal}>
          <div className={classes.videoContent}
               style={{opacity: modalVisible ? 1 : 0, visibility: modalVisible ? 'visible' : 'hidden'}}>
            <div className={`${classes.videoContentAnimation} ${closing ? classes.zoomOut : classes.zoomIn}`}>
              <div ref={fullScreenSliderRef} className={`keen-slider ${classes.fullVideoSlider}`}>
                {videoItems.map((video, index) => (
                  <div key={index} className={`keen-slider__slide ${classes.videoContainer}`}>
                    <div className={classes.fullVideo} onClick={togglePlayPause}>
                      <div className={classes.videoProgressBar} ref={trackRef} onClick={handleTrackClick}>
                        <div className={classes.videoProgressTrack}/>
                        <div
                          className={`${classes.videoProgressKnob} ${isDragging ? 'dragging' : ''}`}
                          style={{left: isDragging ? `${draggedProgress}%` : `${progress}%`}}
                          onMouseDown={handleMouseDown}
                          onTouchStart={handleTouchStart}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'ArrowLeft') {
                              const newProgress = Math.max(progress - 5, 0);
                              setProgress(newProgress);
                              const seekTime = (newProgress / 100) * duration;
                              playerRefs.current[selectedVideo]?.seekTo(seekTime);
                            } else if (e.key === 'ArrowRight') {
                              const newProgress = Math.min(progress + 5, 100);
                              setProgress(newProgress);
                              const seekTime = (newProgress / 100) * duration;
                              playerRefs.current[selectedVideo]?.seekTo(seekTime);
                            }
                          }}
                        />
                      </div>
                      {isBuffering && (
                        <div className={classes.bufferingSpinner}>
                          <div className={classes.spinner}>
                            <div className={`${classes.dot} ${classes.dot1}`}/>
                            <div className={`${classes.dot} ${classes.dot2}`}/>
                            <div className={`${classes.dot} ${classes.dot3}`}/>
                          </div>
                        </div>
                      )}
                      <ReactPlayer
                        key={index}
                        ref={(player) => (playerRefs.current[index] = player)}
                        url={video.field_value.complete_video_url.field_value}
                        playing={selectedVideo === index && !isPaused}
                        muted={isMuted}
                        loop={true}
                        width="100%"
                        height="100%"
                        playsinline
                        onProgress={handleProgress}
                        onDuration={handleDuration}
                        onPlay={() => {
                          setIsBuffering(false);
                          handlePlay(index);
                        }}
                        onPause={() => handlePause(index)}
                        onBuffer={() => setIsBuffering(true)}
                        onBufferEnd={() => setIsBuffering(false)}
                        onError={(e) => {
                          console.error('Error loading video:', e);
                          setIsBuffering(false);
                        }}
                      />
                      {selectedVideo === index && !isBuffering && (
                        <div
                          className={`${classes.playPauseOverlay} ${isPaused ? classes.playPauseOverlayVisible : ''}`}>
                          <IconButton
                            size="small"
                            onClick={() => slider?.current?.prev()}
                            className={classes.playPauseIcon}
                            aria-label={isPaused ? 'pause_black' : 'play_black'}
                          >
                            <Icon className={classes.navBtn}
                                  name={isPaused ? 'pause_black' : 'play_black'}/>
                          </IconButton>
                        </div>
                      )}
                      <div className={classes.modalFooter}>
                        <a className={classes.btnVideoImpact} href={video.field_value?.cta?.field_value?.href}>
                          <CtaButton
                            {...video.field_value.cta}
                            next
                            size="large"
                            trackingObj={repo}
                            trackingAction="click_link"
                            trackingLabel={
                              'cta - ' +
                              video.field_value.cta?.productCode
                            }
                          >
                            {video.field_value.cta?.field_value?.title || ''}
                          </CtaButton>
                        </a>
                      </div>
                    </div>
                    <div className={classes.controlPanel}>
                      <Icon onClick={handleCloseModal} className={classes.controlBtn}
                            name="close_black"/>
                      <Icon onClick={() => setIsMuted((prev) => !prev)} className={`${classes.controlBtn}`}
                            name={isMuted ? 'unmute_black' : 'mute_black'}/>
                      <Icon onClick={handleShare} className={`${classes.controlBtn} ${classes.shareBtn}`}
                            name="share_black"/>
                    </div>
                  </div>
                ))}
              </div>
              <div className={classes.navPanel}>
                <Icon
                  onClick={handlePrev}
                  className={`${classes.controlBtn} icon`}
                  name="arrow_black"
                />
                <div className={classes.rotateIcon180}>
                  <Icon
                    onClick={handleNext}
                    className={`${classes.controlBtn} icon`}
                    name="arrow_black"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Module>
  );
};

// PropTypes for type checking
VideoImpactModule.propTypes = {
  title: PropTypes.shape({field_value: PropTypes.string}),
  item: PropTypes.shape({
    field_value: PropTypes.arrayOf(
      PropTypes.shape({
        field_value: PropTypes.shape({
          product: PropTypes.shape({
            field_value: PropTypes.shape({
              collection_title: PropTypes.shape({field_value: PropTypes.string}),
            }),
          }),
          short_video_url: PropTypes.shape({field_value: PropTypes.string}),
          complete_video_url: PropTypes.shape({field_value: PropTypes.string}),
          cta: PropTypes.shape({
            field_value: PropTypes.shape({
              href: PropTypes.string,
              label: PropTypes.string,
            }),
          }),
        }),
      })
    ),
  }),
};

export default VideoImpactModule;